import React, { useState, useEffect, useRef } from "react";
import { useSelector } from "react-redux";
import get from "lodash/get";
import { format } from "date-fns";
import ReadLaterIcon from "../../../../assets/icons/Readlatericon";
import SortIcon from "../../../../assets/icons/sorticon";
import {
	generateJwtToken,
	getAllBookmarks,
	deleteBookmarkByPageId
} from "../../../../api/utils";
import LoadingIndicator from "../../atoms/loading-indicator";
import "./bookmarks-list.m.css";
import CloseIcon from "../../atoms/CloseIcon";
import { BookmarkRemoveModal } from "../bookmark-remove-modal";
import { STATIC_KEYWORDS } from "../../../constants";

export const BookmarksList = () => {
	const [sortOrder, setSortOrder] = useState("newest");
	const [isMobile, setIsMobile] = useState(false);
	const [showSortModal, setShowSortModal] = useState(false);
	const [bookmarks, setBookmarks] = useState([]);
	const [bookmarksTotalCount, setBookmarksTotalCount] = useState(0);
	const [bookmarksLoading, setBookmarksLoading] = useState(false);
	const [areBookmarksLoaded, setBookmarksLoaded] = useState(false);
	const [bookmarksError, setBookmarksError] = useState(null);
	const [jwtToken, setJwtToken] = useState(null);
	const [visibleCount, setVisibleCount] = useState(10);
	const [isLoadingMore, setIsLoadingMore] = useState(false);
	const [bookmarkToRemove, setBookmarkToRemove] = useState(null);
	const dropdownRef = useRef(null);

	const publisherAttributes = useSelector((state) =>
		get(state, ["qt", "config", "publisher-attributes"], {})
	);

	const metypeConfig = get(publisherAttributes, ["metypeConfig"], {});
	const fallbackImage = get(publisherAttributes, ["fallback-image"], "");
	const member = useSelector((state) => get(state, ["member"], null));

	// ... (existing effects) ...

	const handleRequestRemove = (storyUrl) => {
		setBookmarkToRemove(storyUrl);
	};

	// Check if device is mobile
	useEffect(() => {
		const checkMobile = () => {
			setIsMobile(window.innerWidth <= 767);
		};

		checkMobile();
		window.addEventListener("resize", checkMobile);

		return () => window.removeEventListener("resize", checkMobile);
	}, []);

	// Click outside to close dropdown
	useEffect(() => {
		const handleClickOutside = (event) => {
			if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
				setShowSortModal(false);
			}
		};

		if (showSortModal && !isMobile) {
			document.addEventListener("mousedown", handleClickOutside);
		} else {
			document.removeEventListener("mousedown", handleClickOutside);
		}

		return () => {
			document.removeEventListener("mousedown", handleClickOutside);
		};
	}, [showSortModal, isMobile]);

	// Fetch JWT token when component mounts or member changes
	useEffect(() => {
		const fetchJwtToken = async () => {
			console.log(member, metypeConfig);
			if (member?.id && metypeConfig?.integrationsId) {
				try {
					const token = await generateJwtToken(metypeConfig);
					if (token) setJwtToken(token);
				} catch (error) {
					console.error("Error fetching JWT token:", error);
				}
			} else {
				setJwtToken(null);
			}
		};

		fetchJwtToken();
	}, [member?.id, metypeConfig]);

	// Load bookmarks when JWT token is available
	useEffect(() => {
		if (jwtToken && bookmarks.length === 0 && !bookmarksLoading) {
			loadBookmarks();
		}
	}, [jwtToken]);

	const loadBookmarks = async () => {
		// uncomment before merge
		if (!jwtToken) {
			setBookmarksError("Please log in to view bookmarks");
			return;
		}

		setBookmarksLoading(true);
		setBookmarksError(null);

		try {
			const result = await getAllBookmarks(metypeConfig, jwtToken);
			console.log(result);
			setBookmarks(result.data);
			setBookmarksTotalCount(result.total_count);
		} catch (error) {
			console.error("Error loading bookmarks:", error);
			setBookmarksError(error.message || "Failed to load bookmarks");
		} finally {
			setBookmarksLoading(false);
			setBookmarksLoaded(true);
		}
	};

	const handleDeleteBookmark = async () => {
		const storyUrl = bookmarkToRemove;
		if (!jwtToken) {
			console.error("Please log in to remove bookmarks");
			return;
		}

		try {
			await deleteBookmarkByPageId(metypeConfig, storyUrl, jwtToken);
			// Reload bookmarks to get fresh data
			await loadBookmarks();
		} catch (error) {
			console.error("Error deleting bookmark:", error);
		}

		setBookmarkToRemove(null);
	};

	const handleSortChange = (value) => {
		setSortOrder(value);
		setShowSortModal(false);
		setVisibleCount(10); // Reset to show first 10 when sorting changes
	};

	const handleLoadMore = () => {
		setIsLoadingMore(true);
		// Simulate loading delay for better UX
		setTimeout(() => {
			setVisibleCount((prev) => prev + 10);
			setIsLoadingMore(false);
		}, 300);
	};

	if (!areBookmarksLoaded) {
		return (
			<div styleName="bookmarks-list">
				<div styleName="loading-state">
					<p>Loading bookmarks...</p>
				</div>
			</div>
		);
	}

	if (bookmarksError) {
		return (
			<div styleName="bookmarks-list">
				<div styleName="error-state">
					<p>Error loading bookmarks: {bookmarksError}</p>
					<button styleName="retry-button" onClick={loadBookmarks}>
						Retry
					</button>
				</div>
			</div>
		);
	}
	if (areBookmarksLoaded) {
		if (!bookmarks || bookmarks.length === 0) {
			return (
				<div styleName="bookmarks-list">
					<div styleName="empty-state">
						<p>No bookmarks yet.</p>
						<p styleName="empty-state-subtitle">
							Start bookmarking articles to read them later!
						</p>
					</div>
				</div>
			);
		}
	}

	// Sort bookmarks
	const sortedBookmarks = [...bookmarks].sort((a, b) => {
		const dateA = get(a, "attributes.metadata.published-at", 0);
		const dateB = get(b, "attributes.metadata.published-at", 0);
		return sortOrder === "newest" ? dateB - dateA : dateA - dateB;
	});

	const formatPublishedDate = (timestamp) => {
		if (!timestamp) return "";
		try {
			const date = new Date(timestamp);
			return format(date, "MMM yyyy");
		} catch (error) {
			return "";
		}
	};

	return (
		<div styleName="bookmarks-list">
			{areBookmarksLoaded && (
				<>
					<div styleName="bookmarks-header">
						<div styleName="bookmark-count">
							{bookmarksTotalCount || bookmarks.length} bookmark
							{(bookmarksTotalCount || bookmarks.length) !== 1 ? "s" : ""}
						</div>
						<div styleName="sort-container" ref={dropdownRef}>
							<button
								styleName="sort-button"
								onClick={() => setShowSortModal(!showSortModal)}
							>
								<SortIcon width={15} height={14} color="#242424" />
								<span>{sortOrder === "newest" ? "Newest" : "Oldest"}</span>
							</button>

							{!isMobile && showSortModal && (
								<div styleName="sort-dropdown-menu">
									<div styleName="dropdown-options-wrapper">
										<button
											styleName={
												sortOrder === "newest"
													? "dropdown-option active"
													: "dropdown-option"
											}
											onClick={() => handleSortChange("newest")}
										>
											Newest
										</button>
										<button
											styleName={
												sortOrder === "oldest"
													? "dropdown-option active"
													: "dropdown-option"
											}
											onClick={() => handleSortChange("oldest")}
										>
											Oldest
										</button>
									</div>
								</div>
							)}
						</div>
					</div>

					{/* Mobile Sort Modal (Bottom Sheet) */}
					{isMobile && showSortModal && (
						<>
							<div
								styleName="modal-overlay"
								onClick={() => setShowSortModal(false)}
							/>
							<div styleName="sort-modal">
								<div styleName="modal-header">
									<h3 styleName="modal-title">Sort by</h3>
									<button
										styleName="modal-close"
										onClick={() => setShowSortModal(false)}
										aria-label="Close"
									>
										<CloseIcon color="#242424" size={12} strokeWidth={1.25} />
									</button>
								</div>
								<div styleName="modal-options-wrapper">
									<button
										styleName={
											sortOrder === "newest"
												? "modal-option active"
												: "modal-option"
										}
										onClick={() => handleSortChange("newest")}
									>
										Newest
									</button>
									<button
										styleName={
											sortOrder === "oldest"
												? "modal-option active"
												: "modal-option"
										}
										onClick={() => handleSortChange("oldest")}
									>
										Oldest
									</button>
								</div>
							</div>
						</>
					)}

					<div styleName="bookmarks-items">
						{sortedBookmarks.slice(0, visibleCount).map((bookmark) => {
							const metadata = get(bookmark, "attributes.metadata", {});
							const headline = metadata.headline || "Untitled";
							const subheadline = metadata.subheadline || "";
							const url = metadata.url || "#";
							const heroImage = metadata["hero-image-s3-key"];
							const publishedAt = metadata["published-at"];
							const sectionName = metadata.sectionName || "Growth & Innovation";
							const sectionUrl = metadata.sectionUrl || "/";
							const desktopSvg =
								metadata["desktop-svg"] ||
								"https://images.assettype.com/ima-india-uat/2025-11-05/mtp4vcly/bu.svg";
							const storyTemplate = metadata["story-template"];
							const isPremium = storyTemplate === "premium-story-template";

							// Find Entity tag from tags array to get the topic slug
							let tags = metadata.tags || [];

							if (typeof tags === "string") {
								try {
									tags = JSON.parse(tags);
								} catch (e) {
									console.error("Error parsing tags:", e);
									tags = [];
								}
							}

							if (!Array.isArray(tags)) {
								tags = [];
							}
							const entityTag = tags.find(
								(tag) => tag["tag-type"] === "Entity"
							);
							const topicUrl = entityTag?.slug
								? `/topic/${entityTag.slug}`
								: url;

							return (
								<div key={bookmark.id} styleName="bookmark-item">
									<div styleName="bookmark-left">
										{desktopSvg && (
											<div styleName="section-icon">
												<a href={topicUrl}>
													<img
														styleName="section-icon-image"
														src={desktopSvg}
														alt={sectionName}
													/>
												</a>
											</div>
										)}

										<div styleName="hero-image-container">
											<a href={url}>
												{heroImage ? (
													<img
														styleName="hero-image"
														src={`https://gumlet.assettype.com/${heroImage}?w=150&h=100&auto=format,compress`}
														alt={headline}
													/>
												) : fallbackImage ? (
													<img
														styleName="hero-image"
														src={fallbackImage}
														alt={headline}
													/>
												) : (
													<img
														styleName="hero-image"
														src="/icons/fallback_img.png"
														alt={headline}
													/>
												)}
											</a>
										</div>

										<div styleName="bookmark-text">
											{sectionName && (
												<a href={sectionUrl} styleName="section-name-link">
													<div styleName="section-name">
														{sectionName.toUpperCase()}
													</div>
												</a>
											)}
											<a href={url} styleName="headline-link">
												<h3 styleName="headline">{headline}</h3>
											</a>
											{subheadline && (
												<a href={url} styleName="headline-link">
													<p styleName="subheadline">{subheadline}</p>
												</a>
											)}
										</div>
									</div>

									<div styleName="bookmark-right">
										<div styleName="icon-container">
											<button
												styleName="bookmark-icon-btn"
												onClick={() => handleRequestRemove(url)}
												title="Remove bookmark"
												aria-label="Remove bookmark"
											>
												<ReadLaterIcon
													width={12.444}
													height={16}
													variant="bookmarks"
													fill="#FF2626"
												/>
											</button>
											{bookmarkToRemove === url && (
												<BookmarkRemoveModal
													onConfirm={handleDeleteBookmark}
													onCancel={() => setBookmarkToRemove(null)}
												/>
											)}
										</div>
										<div styleName="date_premium">
											<div styleName="date">
												{formatPublishedDate(publishedAt)}
											</div>
											{isPremium && (
												<div styleName="premium-badge">
													{STATIC_KEYWORDS.PREMIUM}
												</div>
											)}
										</div>
									</div>
								</div>
							);
						})}
					</div>
					{visibleCount < sortedBookmarks.length && (
						<div styleName="load-more-container">
							<button
								styleName="load-more-button"
								onClick={handleLoadMore}
								disabled={isLoadingMore}
							>
								{isLoadingMore ? "Loading..." : "Load More"}
							</button>
						</div>
					)}
				</>
			)}
		</div>
	);
};
